import { PlusIcon } from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import type { ContractDto } from '@/api/generated/schemas';
import {
  DataTable,
  type DataTableColumn,
  EmptyState,
  ErrorState,
  LoadingSpinner,
  PageHeader,
  PaginationControls,
} from '@/components/common';
import {
  AddContractModal,
  ContractFilters,
  ContractSourceChip,
  ContractStatusChip,
  ContractTypeAndSegmentChips,
} from '@/components/contracts';
import { useDebounce } from '@/hooks/useDebounce';
import { usePaginatedSearch } from '@/hooks/usePaginatedSearch';
import { useContracts } from '@/queries/contracts.queries';
import { useSearchContracts } from '@/queries/search.queries';
import { type ContractFilterValues, countActiveContractFilters, emptyContractFilters } from '@/utils/contract-filters';
import { getState, issuerLabel } from '@/utils/contract.utils';
import { formatDate } from '@/utils/formatting';

export const Route = createFileRoute('/contracts/')({
  component: Contracts,
});

function Contracts() {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [filters, setFilters] = useState<ContractFilterValues>(emptyContractFilters);
  // Filters carry free-text fields, so the whole set is debounced rather than
  // firing a query per keystroke.
  const debouncedFilters = useDebounce(filters, 300);
  const activeFilterCount = countActiveContractFilters(debouncedFilters);

  const pagination = usePaginatedSearch<ContractDto>({ filtersActive: activeFilterCount > 0 });
  const paginatedQuery = useContracts({ cursor: pagination.cursor, pageSize: pagination.pageSize });
  const searchQuery = useSearchContracts(pagination.debouncedSearch, pagination.searchPage, debouncedFilters);
  const {
    data: contracts,
    isLoading,
    error,
    hasNextPage,
    hasPreviousPage,
    handleNextPage,
    handlePreviousPage,
    isFetching,
  } = pagination.withQueries({ paginatedQuery, searchQuery });

  // A new filter set invalidates the page the user is on.
  const handleFiltersChange = (next: ContractFilterValues) => {
    setFilters(next);
    pagination.resetPagination();
  };

  const hasQuery = pagination.searchQuery.trim().length > 0 || activeFilterCount > 0;

  // Define table columns
  const columns: DataTableColumn<ContractDto>[] = [
    {
      key: 'issuer',
      label: 'Issuer',
      render: (contract) => (
        <div className="flex items-center gap-2">
          <div className="text-sm text-default-900">{issuerLabel(contract.issuer)}</div>
          <ContractSourceChip contract={contract} />
        </div>
      ),
    },
    {
      key: 'contractName',
      label: 'Name',
      render: (contract) => <div className="text-sm text-default-900">{contract.contractName ?? '—'}</div>,
    },
    {
      key: 'customer',
      label: 'Customer',
      // Only search results carry customer names — they come off the search index,
      // not the contract. A browse shows a dash rather than a blank column.
      render: (contract) => <div className="text-sm text-default-900">{contract.customerNames?.join(', ') ?? '—'}</div>,
    },
    {
      key: 'type',
      label: 'Type',
      render: (contract) => <ContractTypeAndSegmentChips contract={contract} />,
    },
    {
      key: 'state',
      label: 'State',
      render: (contract) => <div className="text-sm text-default-900">{getState(contract)}</div>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (contract) => <ContractStatusChip contract={contract} />,
    },
    {
      key: 'startDate',
      label: 'Start Date',
      render: (contract) => <div className="text-sm text-default-900">{formatDate(contract.startDate)}</div>,
    },
    {
      key: 'endDate',
      label: 'End Date',
      render: (contract) => <div className="text-sm text-default-900">{formatDate(contract.endDate)}</div>,
    },
  ];

  return (
    <div className="py-6">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <PageHeader title="Contracts" description="View all energy contracts and agreements" />
          <Button color="primary" startContent={<PlusIcon className="h-4 w-4" />} onPress={() => setIsAddOpen(true)}>
            Add Contract
          </Button>
        </div>

        <ContractFilters
          values={filters}
          onChange={handleFiltersChange}
          search={pagination.searchQuery}
          onSearchChange={pagination.handleSearchChange}
        />

        {/* Loading State */}
        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <ErrorState title="Failed to load contracts" message="Please try again later" />
        ) : contracts.length > 0 ? (
          <>
            <DataTable
              columns={columns}
              data={contracts}
              getRowKey={(contract) => contract.id}
              getRowHref={(contract) => `/contracts/${contract.id}`}
              aria-label="Contracts"
            />
            <PaginationControls
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              onNextPage={handleNextPage}
              onPreviousPage={handlePreviousPage}
              isLoading={isFetching}
            />
          </>
        ) : (
          <EmptyState
            icon={PlusIcon}
            title={hasQuery ? 'No contracts found matching your search' : 'No contracts added yet'}
            description={
              hasQuery
                ? 'Try adjusting your search terms or clearing some filters'
                : 'Contracts will appear here once created'
            }
            variant={hasQuery ? 'search' : 'default'}
          />
        )}
      </div>
      <AddContractModal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} />
    </div>
  );
}
